import { useSyncExternalStore } from "react";

/**
 * 차트 색·글꼴을 디자인 토큰(CSS 변수)에서 읽는다. 라이트/다크·강조색이 바뀌면(<html data-theme|data-accent|data-design>, OS 테마) 다시 읽고
 * 모든 차트가 새 테마로 다시 그려진다. Canvas 는 CSS 변수를 모르므로 실제 색 값으로 풀어서 넘긴다.
 */
export type ChartTheme = {
  dark: boolean;
  font: string;
  text: string;
  muted: string;
  faint: string;
  border: string;
  surface: string;
  surfaceMuted: string;
  accent: string;
  ok: string;
  fail: string;
  warn: string;
  /** 계열 구분용 색 (의미 없는 순서 색). 위험 색(빨강)은 넣지 않는다 — 실패처럼 보이지 않게 */
  palette: string[];
  /** "var(--x)" 나 일반 CSS 색을 캔버스용 값으로 */
  color: (css: string | undefined, index?: number) => string;
};

/** 의미가 있는 색 이름 (성공·실패 등). 차트 계열의 color 에 그대로 넣는다 */
export const tone = { ok: "var(--success)", fail: "var(--danger)", warn: "var(--warning)", muted: "var(--text-faint)", main: "var(--accent)" } as const;

// 토큰 밖의 보조 색. 라이트·다크 모두에서 배경과 대비가 나는 중간 밝기
const EXTRA = ["#14b8a6", "#f59e0b", "#8b5cf6", "#0ea5e9", "#ec4899", "#84cc16", "#64748b", "#f97316"];

let probe: HTMLSpanElement | null = null;
const resolved = new Map<string, string>();
function resolveCss(css: string): string {
  if (typeof document === "undefined") return css;
  if (!css.includes("var(") && !css.includes("color-mix")) return css;
  const hit = resolved.get(css);
  if (hit) return hit;
  if (!probe) {
    probe = document.createElement("span");
    probe.style.display = "none";
    document.body.appendChild(probe);
  }
  probe.style.color = "";
  probe.style.color = css;
  const out = getComputedStyle(probe).color || css;
  resolved.set(css, out);
  return out;
}

function read(): ChartTheme {
  resolved.clear();
  const root = document.documentElement;
  const dark = root.dataset.theme === "dark" || (!root.dataset.theme && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  const v = (name: string, fallback: string) => resolveCss(`var(${name}, ${fallback})`);
  const accent = v("--accent", "#3b6ef5");
  const palette = [accent, ...EXTRA];
  const font = getComputedStyle(document.body).fontFamily || "sans-serif";
  return {
    dark: !!dark,
    font,
    text: v("--text", dark ? "#f0f0f0" : "#202d27"),
    muted: v("--text-muted", dark ? "#a0a8a4" : "#5f6b66"),
    faint: v("--text-faint", dark ? "#6b7570" : "#9aa39f"),
    border: v("--border", dark ? "#2e3833" : "#e3e8e5"),
    surface: v("--surface", dark ? "#1c2420" : "#ffffff"),
    surfaceMuted: v("--surface-muted", dark ? "#232c28" : "#f4f6f5"),
    accent,
    ok: v("--success", "#23866f"),
    fail: v("--danger", "#c4473a"),
    warn: v("--warning", "#cf9b3d"),
    palette,
    color: (css, index = 0) => (css ? resolveCss(css) : palette[index % palette.length]!),
  };
}

let current: ChartTheme | null = null;
const listeners = new Set<() => void>();
let watching = false;
function watch() {
  if (watching || typeof window === "undefined") return;
  watching = true;
  const bump = () => {
    current = null;
    listeners.forEach((l) => l());
  };
  new MutationObserver(bump).observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "data-accent", "data-design", "class", "style"] });
  window.matchMedia?.("(prefers-color-scheme: dark)").addEventListener?.("change", bump);
}

function subscribe(l: () => void) {
  watch();
  listeners.add(l);
  return () => listeners.delete(l);
}
function snapshot(): ChartTheme {
  if (!current) current = read();
  return current;
}

/** 지금 테마 (테마가 바뀌면 새 객체 → 이 값을 의존성으로 옵션을 다시 만든다) */
export function useChartTheme(): ChartTheme {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
